import { useState } from 'react'
import { AlertCircle, Info } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '@/redux'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import {
  selectAllRegistryDomainsOrdered,
  selectDashboardDomainRegistryStatus,
} from '@/redux/slice/dashboardDomainRegistrySlice'
import { flowTransitionThunk } from '@/services/DashboardFlowTransitionService'
import { buildDomainPickTemplates } from '@/services/transitionTemplates'
import { Button } from '@/components/ui/button'
import { resolveColorSchemeClass } from './colorSchemeMap'
import { resolveIcon } from './iconMap'
import { UnavailableDomainInfoModal } from './UnavailableDomainInfoModal'
import type { DashboardDomainRegistryItem } from '@/types/DashboardDomainRegistry.types'

/**
 * DomainPickerBlock — FSM gate: awaiting_domain (Q3.5)
 *
 * Mockup: designs/ai-dashboard-platform-v3/2026-04-23-11-domain-picker-v3.html
 * Availability treatment: designs/credential-aware-dashboard-availability/v2/
 *   2026-05-26-dashboard-picker-mixed-states-v2.html (Slice 2.3 FE)
 *
 * The picker is registry-driven post-Slice 4 of EPIC-132's metadata refactor
 * (TDD plans/2026-05-08-slice-D-staff-with-domain-metadata-refactor-tdd.md).
 * The hardcoded `DOMAINS[]` array was deleted; cards render from the SDK-owned
 * domain definitions returned by `GET /v1/dashboard-domains`. v1 publishes
 * 4 cards: Enrollment, Financial (disabled stub), Bunks (disabled stub), Staff
 * — Bunks and Financial render grayed/non-clickable until their primitives
 * ship in Slices B/C (TDD §6.2b).
 *
 * Slice 2.3 FE adds credential-aware availability treatment:
 *   - domain.disabled (feature stub): "NOT AVAILABLE" all-caps, no info icon,
 *     uses <button disabled> (no interactive child)
 *   - availability.state === 'unavailable' (credential-aware): sentence-case
 *     "Not available" badge + info icon, uses <div role="checkbox" aria-disabled>
 *     (info icon must be separately focusable — HTML spec forbids interactive
 *     children inside <button disabled>, see brand-guide §37a)
 *   - availability absent / 'unknown': fail-open, render as available
 *
 * Visual chrome (Tailwind classes, ARIA labels, checkbox indicator) is
 * preserved verbatim from the prior hardcoded implementation — the refactor
 * is registry-driven rendering with zero visible change for non-unavailable cards.
 */

// ─── Check icon SVG ────────────────────────────────────────────────────────────

const CheckSVG = () => (
  <svg
    viewBox="0 0 9 6"
    width="9"
    height="6"
    fill="none"
    stroke="white"
    strokeWidth="1.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M1 3l2 2 5-4.5" />
  </svg>
)

// ─── Loading skeleton + error well ────────────────────────────────────────────

/**
 * Lightweight grid-shaped skeleton shown while the registry is loading or
 * idle. Mirrors the 2×2 grid geometry so the layout doesn't reflow when cards
 * paint in.
 */
const DomainPickerSkeleton = () => (
  <div data-testid="domain-picker-block" className="flex flex-col gap-3 w-full">
    <div
      className="grid grid-cols-2 gap-2 max-w-[540px]"
      role="status"
      aria-busy="true"
      aria-label="Loading data domains"
    >
      {[0, 1, 2, 3].map(i => (
        <div
          key={i}
          className="h-[78px] rounded-[12px] border-[1.5px] border-border bg-muted animate-pulse"
        />
      ))}
    </div>
  </div>
)

const DomainPickerErrorWell = () => (
  <div
    data-testid="domain-picker-block"
    role="alert"
    className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3.5 flex flex-col gap-2 max-w-[540px]"
  >
    <div className="flex items-center gap-2.5">
      <AlertCircle size={18} className="text-destructive flex-shrink-0" aria-hidden="true" />
      <span className="text-sm font-semibold text-primary">
        Couldn&apos;t load the dashboard catalog
      </span>
    </div>
    <p className="text-xs text-secondary leading-relaxed">
      We couldn&apos;t load the available data domains. Refresh the page to try again.
    </p>
  </div>
)

// ─── DomainCard — Available (and disabled-stub) variant ───────────────────────

/**
 * AvailableDomainCard — renders as `<button>` (the standard interactive pattern).
 * Used for:
 *   - selectable domains (availability absent / unknown / available)
 *   - feature-stub disabled domains (domain.disabled === true, no info icon)
 *
 * The `disabled` prop maps directly to the native HTML disabled attribute.
 * React 18 suppresses onClick on disabled buttons — no dead-guard needed.
 */
interface AvailableDomainCardProps {
  domain: DashboardDomainRegistryItem
  isSelected: boolean
  isDisabled: boolean
  iconEmoji: string
  iconBgClass: string
  ariaLabel: string
  onClick: () => void
}

const AvailableDomainCard = ({
  domain,
  isSelected,
  isDisabled,
  iconEmoji,
  iconBgClass,
  ariaLabel,
  onClick,
}: AvailableDomainCardProps) => (
  <button
    key={domain.pickerKey}
    type="button"
    aria-pressed={isDisabled ? undefined : isSelected}
    aria-label={ariaLabel}
    aria-disabled={isDisabled}
    disabled={isDisabled}
    onClick={onClick}
    className={`flex items-start gap-2.5 px-3.5 py-3.5 rounded-[12px] border-[1.5px] transition-all duration-150 text-left relative focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2
      ${
        isDisabled
          ? 'border-border bg-card opacity-60 cursor-not-allowed'
          : isSelected
          ? 'border-accent bg-accent-background'
          : 'border-border bg-card hover:border-accent hover:bg-accent-background'
      }`}
  >
    {/* Checkbox indicator (top-right) — hidden for disabled stub cards */}
    {!isDisabled && (
      <div
        className={`absolute top-[9px] right-[9px] w-4 h-4 rounded flex items-center justify-center border-[1.5px] transition-all duration-150
          ${isSelected ? 'bg-accent border-accent' : 'bg-card border-border'}`}
        aria-hidden="true"
      >
        {isSelected && <CheckSVG />}
      </div>
    )}

    {/* "NOT AVAILABLE" badge for feature-stub disabled cards (domain.disabled) */}
    {isDisabled && (
      <span
        className="absolute top-[9px] right-[9px] text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted border border-border rounded-[6px] px-1.5 py-0.5"
        aria-hidden="true"
      >
        Not available
      </span>
    )}

    {/* Domain icon */}
    <div
      className={`w-9 h-9 rounded-[9px] flex items-center justify-center flex-shrink-0 text-[18px] ${iconBgClass}`}
      aria-hidden="true"
    >
      {iconEmoji}
    </div>

    {/* Domain info */}
    <div className={isDisabled ? 'flex-1 pr-5' : 'flex-1 pr-7'}>
      <p
        className={`text-sm font-semibold mb-0.5 ${
          isDisabled
            ? 'text-secondary'
            : isSelected
            ? 'text-accent'
            : 'text-primary'
        }`}
      >
        {domain.displayName}
      </p>
      {domain.tagline && (
        <p className="text-xs text-secondary leading-snug">{domain.tagline}</p>
      )}
    </div>
  </button>
)

// ─── DomainCard — Credential-aware Unavailable variant ────────────────────────

/**
 * UnavailableDomainCard — renders as `<div role="checkbox" aria-disabled>`.
 *
 * WHY NOT `<button disabled>`:
 * HTML spec forbids interactive children inside `<button disabled>`. The info
 * icon button inside this card MUST be separately focusable (brand-guide §37a).
 * The solution per §37a is to use `<div role="checkbox" aria-disabled>` for
 * the card wrapper, with the info icon `<button>` as a sibling positioned
 * absolutely with `pointer-events: auto` and `tabIndex={0}`.
 *
 * ARIA contract (§37a):
 *   - Card: role="checkbox" aria-checked="false" aria-disabled="true" tabIndex={-1}
 *   - Info icon: <button> with aria-label, tabIndex={0}
 *   - Badge: aria-hidden="true" (card aria-label carries the semantic)
 *
 * TAB ORDER: info icon is reachable via Tab; card div is skipped (tabIndex=-1).
 *
 * This pattern is for availability.state === 'unavailable' ONLY. Feature stubs
 * (domain.disabled === true) use AvailableDomainCard with disabled={true}.
 */
interface UnavailableDomainCardProps {
  domain: DashboardDomainRegistryItem
  iconEmoji: string
  iconBgClass: string
  onInfoClick: () => void
}

const UnavailableDomainCard = ({
  domain,
  iconEmoji,
  iconBgClass,
  onInfoClick,
}: UnavailableDomainCardProps) => (
  <div
    role="checkbox"
    aria-checked={false}
    aria-disabled={true}
    aria-label={`${domain.displayName}${domain.tagline ? ` — ${domain.tagline}` : ''}. Not available with your current credential.`}
    tabIndex={-1}
    className="flex items-start gap-2.5 px-3.5 py-3.5 rounded-[12px] border-[1.5px] border-border bg-card opacity-60 cursor-not-allowed relative text-left transition-all duration-150"
  >
    {/*
      Unavailable controls (badge + info icon) — positioned top-right.
      Brand-guide §37a: top:9px right:9px for all cards.
      Info icon is 20×20px (was 22×22px in v1 per mockup v2 fix notes).
      Badge max-width: 82px (v2 fix — prevents pushing icon off right edge).
    */}
    <div className="absolute top-[9px] right-[9px] flex items-center gap-1">
      <span
        className="text-[10px] font-semibold text-muted-foreground bg-muted border border-border rounded-[6px] px-[5px] py-0.5 whitespace-nowrap max-w-[82px] leading-[1.4]"
        aria-hidden="true"
      >
        Not available
      </span>
      {/*
        Info icon button — separately focusable (§37a).
        pointer-events: auto is the default; the card body has cursor:not-allowed
        via Tailwind `cursor-not-allowed` on the parent, but this button overrides
        it with its own cursor:pointer via standard button styling.
      */}
      <button
        type="button"
        className="w-[20px] h-[20px] flex items-center justify-center rounded-[4px] text-muted-foreground hover:bg-muted hover:text-accent focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2 transition-colors duration-150 cursor-pointer"
        aria-label={`Why is ${domain.displayName} not available? See details`}
        onClick={onInfoClick}
        tabIndex={0}
      >
        <Info size={14} aria-hidden="true" />
      </button>
    </div>

    {/* Domain icon */}
    <div
      className={`w-9 h-9 rounded-[9px] flex items-center justify-center flex-shrink-0 text-[18px] ${iconBgClass}`}
      aria-hidden="true"
    >
      {iconEmoji}
    </div>

    {/*
      Domain info text block.
      Brand-guide §37a + mockup v2: text div uses padding-right: 88px for
      unavailable cards (fixed reserve for badge max-width 82px + 4px gap +
      icon 20px ≈ 88px right-side reservation).
      Available cards use pr-7 (28px = 16px checkbox + 12px margin).
    */}
    <div className="flex-1 pr-[88px]">
      <p className="text-sm font-semibold text-secondary mb-0.5">
        {domain.displayName}
      </p>
      {domain.tagline && (
        <p className="text-xs text-secondary leading-snug">
          {domain.tagline}
        </p>
      )}
    </div>
  </div>
)

// ─── DomainPickerBlock ─────────────────────────────────────────────────────────

export const DomainPickerBlock = () => {
  const dispatch: AppDispatch = useDispatch()
  const selectedDomainIds = useSelector(
    (state: RootState) => state.dashboardCreation.selectedDomainIds,
  )
  const token = useSelector((s: RootState) => s.user.token)
  const dashboardId = useSelector((s: RootState) => s.dashboardCreation.dashboardId)
  const registryStatus = useSelector(selectDashboardDomainRegistryStatus)
  const domains = useSelector(selectAllRegistryDomainsOrdered)

  // Info modal state — which domain's modal is open (by pickerKey), or null.
  const [modalDomain, setModalDomain] = useState<DashboardDomainRegistryItem | null>(null)

  if (registryStatus === 'idle' || registryStatus === 'loading') {
    return <DomainPickerSkeleton />
  }
  if (registryStatus === 'error' || domains.length === 0) {
    return <DomainPickerErrorWell />
  }

  const handleToggle = (domain: DashboardDomainRegistryItem) => {
    // Disabled-domain protection lives on the rendered <button disabled={isDisabled}>;
    // React 18 suppresses onClick on natively-disabled buttons so a defensive
    // `if (domain.disabled) return` here would be unreachable dead code.
    // See CTO memory feedback_react18_disabled_button_dead_guard.
    dispatch(dashboardCreationActions.toggleDomainId(domain.pickerKey))
  }

  const handleConfirm = () => {
    // The Confirm button is rendered with disabled={selectedDomainIds.length === 0};
    // React 18 suppresses onClick on natively-disabled buttons, so a defensive
    // `if (selectedDomainIds.length === 0) return` here would be unreachable.
    // See CTO memory feedback_react18_disabled_button_dead_guard.
    dispatch(dashboardCreationActions.setFlowState('awaiting_filters'))
    if (token && dashboardId) {
      // Filter to known + non-disabled registry items. A picker key with no
      // registry hit (very rare — registry desync) is silently dropped from the
      // narration; the FSM still advances on the picker keys we have.
      const selectedDomains = selectedDomainIds
        .map(id => domains.find(d => d.pickerKey === id))
        .filter(
          (d): d is DashboardDomainRegistryItem => d !== undefined && !d.disabled,
        )
      const templates = buildDomainPickTemplates(selectedDomains)
      const idempotencyKey =
        globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)
      void dispatch(
        flowTransitionThunk(
          token,
          dashboardId,
          {
            toState: 'awaiting_filters',
            contextDelta: { selectedDomainIds },
            ...templates,
          },
          idempotencyKey,
        ),
      )
    }
  }

  // Confirm button label describes selection.
  const firstSelected = domains.find(d => d.pickerKey === selectedDomainIds[0])
  const confirmLabel =
    selectedDomainIds.length === 0
      ? 'Select at least one domain'
      : selectedDomainIds.length === 1
      ? `Confirm — Set up ${firstSelected?.displayName ?? ''} filters`
      : `Confirm — Set up ${selectedDomainIds.length} domains`

  return (
    <div data-testid="domain-picker-block" className="flex flex-col gap-3 w-full">
      {/* Domain card grid — n cards, 2-column layout */}
      <div
        className="grid grid-cols-2 gap-2 max-w-[540px]"
        role="group"
        aria-label="Select data domains (multi-select)"
      >
        {domains.map(domain => {
          const isSelected = selectedDomainIds.includes(domain.pickerKey)
          const isFeatureStubDisabled = domain.disabled === true
          const iconEmoji = resolveIcon(domain.iconKey)
          const iconBgClass = resolveColorSchemeClass(domain.colorScheme)

          // Credential-aware availability logic (Slice 2.3):
          // - absent / unknown → fail-open, render as available
          // - unavailable → disabled card with info icon (§37a)
          const availability = domain.availability
          const isCredentialUnavailable =
            availability?.state === 'unavailable'

          // Feature stubs (domain.disabled) use the standard <button disabled>
          // pattern — they have no info icon, no interactive child.
          if (isFeatureStubDisabled) {
            const ariaLabel = `${domain.displayName} — ${domain.tagline ?? ''}. Not available.`
            return (
              <AvailableDomainCard
                key={domain.pickerKey}
                domain={domain}
                isSelected={false}
                isDisabled={true}
                iconEmoji={iconEmoji}
                iconBgClass={iconBgClass}
                ariaLabel={ariaLabel}
                onClick={/* istanbul ignore next */ () => {}}
              />
            )
          }

          // Credential-aware unavailable domains use <div role="checkbox" aria-disabled>
          // (§37a) — the info icon inside must be separately focusable.
          if (isCredentialUnavailable) {
            return (
              <UnavailableDomainCard
                key={domain.pickerKey}
                domain={domain}
                iconEmoji={iconEmoji}
                iconBgClass={iconBgClass}
                onInfoClick={() => setModalDomain(domain)}
              />
            )
          }

          // Normal available card — selectable <button>.
          const ariaLabel = `${domain.displayName} — ${domain.tagline ?? ''}${isSelected ? '. Selected.' : ''}`
          return (
            <AvailableDomainCard
              key={domain.pickerKey}
              domain={domain}
              isSelected={isSelected}
              isDisabled={false}
              iconEmoji={iconEmoji}
              iconBgClass={iconBgClass}
              ariaLabel={ariaLabel}
              onClick={() => handleToggle(domain)}
            />
          )
        })}
      </div>

      {/* Confirm button */}
      <div className="max-w-[540px] mt-1">
        <Button
          type="button"
          className="w-full font-semibold"
          disabled={selectedDomainIds.length === 0}
          aria-label={confirmLabel}
          onClick={handleConfirm}
        >
          {confirmLabel}
        </Button>
      </div>

      {/* Info modal — credential-aware unavailable card (§37b) */}
      {modalDomain !== null && modalDomain.availability !== undefined && (
        <UnavailableDomainInfoModal
          // The runtime guard above (availability !== undefined) satisfies the
          // narrowed prop type — TypeScript cannot infer narrowing across JSX
          // conditional gates, so we cast explicitly here.
          domain={
            modalDomain as DashboardDomainRegistryItem & {
              availability: NonNullable<DashboardDomainRegistryItem['availability']>
            }
          }
          iconEmoji={resolveIcon(modalDomain.iconKey)}
          open={true}
          onClose={() => setModalDomain(null)}
        />
      )}
    </div>
  )
}
